import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import FatturazioneSdiTab from "./FatturazioneSdiTab";
import FatturazioneControlloMensileTab from "./FatturazioneControlloMensileTab";
import FatturazioneFattureAttiveTab from "./FatturazioneFattureAttiveTab";
import FatturazioneNoteCreditoTab from "./FatturazioneNoteCreditoTab";
import FatturazioneKpiRow from "./FatturazioneKpiRow";
import FatturazioneControlloCostiTab from "./FatturazioneControlloCostiTab";
import { labelDestinatarioOspite } from "../utils/fatturazioneLabels";
import {
  Receipt,
  Loader2,
  RefreshCw,
  Filter,
  FileText,
  Download,
  Send,
  Inbox,
  Trash2,
  Eye,
  Search,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  CheckCircle2,
  FileCheck,
  Building2,
  LayoutDashboard,
  MoreVertical,
  Circle,
  AlertCircle,
  Clock3,
  FileX,
  ExternalLink,
  Euro,
  Hourglass,
  Calendar,
  Ban,
  Clock,
  Plus,
  FileUp,
  FileMinus2,
  TrendingUp,
  ArrowDownUp,
} from "lucide-react";

/** Stato riga tab «Fatture generate» (allineato alla preview). */
function statoUiGenerate(stato, fattura) {
  if (fattura?.sdi_coda_at) return "in_coda_sdi";
  if (stato === "stornata") return "stornata";
  if (["rifiutata", "scartata"].includes(stato)) return "annullata";
  if (["inviata_sdi", "consegnata", "accettata"].includes(stato)) return "inviata";
  if (stato === "bozza") return "generata";
  if (stato === "generata") return "da_inviare";
  return "generata";
}

const STATO_FATTURA_GENERATE_UI = {
  bozza: { label: "Bozza", className: "bg-slate-100 text-slate-700", Icon: Circle },
  da_verificare: { label: "Da verificare", className: "bg-amber-50 text-amber-800", Icon: AlertCircle },
  in_coda_sdi: {
    label: "In coda SDI",
    className: "bg-sky-50 text-sky-800 ring-1 ring-sky-200",
    Icon: Loader2,
    spinning: true,
  },
  inviata: { label: "Inviata", className: "bg-blue-50 text-blue-800", Icon: Send },
  annullata: { label: "Annullata", className: "bg-slate-100 text-slate-600", Icon: FileX },
  stornata: { label: "Stornata", className: "bg-purple-50 text-purple-800", Icon: FileX },
};

const STATO_PAGAMENTO_GENERATE_UI = {
  pagata: { label: "Pagata", className: "bg-emerald-50 text-emerald-800", Icon: CheckCircle2 },
  parziale: { label: "Parziale", className: "bg-violet-50 text-violet-800", Icon: Clock3 },
  da_incassare: { label: "Da incassare", className: "bg-orange-50 text-orange-800", Icon: Clock },
  scaduta: { label: "Scaduta", className: "bg-red-50 text-red-800", Icon: Ban },
  non_pagata: { label: "Da incassare", className: "bg-orange-50 text-orange-800", Icon: Clock },
};

function statoFatturaGenerateKey(stato, fattura) {
  if (fattura?.sdi_coda_at) return "in_coda_sdi";
  if (stato === "stornata") return "stornata";
  if (["rifiutata", "scartata"].includes(stato)) return "annullata";
  if (["inviata_sdi", "consegnata", "accettata"].includes(stato)) return "inviata";
  if (stato === "bozza") return "da_verificare";
  if (stato === "generata" || stato === "approvata") return "bozza";
  return "bozza";
}

function statoPagamentoGenerateKey(stato, fattura) {
  if (stato === "pagata") return "pagata";
  if (stato === "parziale") return "parziale";
  if (stato === "scaduta") return "scaduta";
  const scad = String(fattura?.data_scadenza || "").slice(0, 10);
  if (scad && /^\d{4}-\d{2}-\d{2}$/.test(scad)) {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    if (scad < todayStr) return "scaduta";
  }
  return "da_incassare";
}

const ACTION_BTN =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:pointer-events-none disabled:opacity-50";

function StatoBadgeGenerate({ stato, map, resolver, item }) {
  const key = resolver.length > 1 ? resolver(stato, item) : resolver(stato);
  const cfg = map[key] || map.bozza;
  const Icon = cfg.Icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
        cfg.className,
      )}
      title={key === "in_coda_sdi" ? "Invio allo SDI in corso in background" : undefined}
    >
      {Icon && (
        <Icon
          className={cn("w-3.5 h-3.5 shrink-0", cfg.spinning && "animate-spin")}
          strokeWidth={2}
          aria-hidden
        />
      )}
      {cfg.label}
    </span>
  );
}

function formatPeriodoFattura(f) {
  if (f.mese) {
    const [y, m] = f.mese.split("-");
    const mesi = ["", "Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
    const label = mesi[parseInt(m, 10)] || m;
    return `${label} ${y}`;
  }
  return "—";
}

function AvatarInizialiOspite({ nome }) {
  const parts = (nome || "").trim().split(/\s+/).filter(Boolean);
  const ini =
    parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : (parts[0]?.slice(0, 2) || "?").toUpperCase();
  return (
    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold shrink-0">
      {ini}
    </span>
  );
}

function FatturaGenerateAzioni({
  f,
  openDetail,
  handleInviaSdi,
  sendingSdi,
  canInviaSdi,
  handleDownloadXml,
  handleOpenFatturaPdf,
  handleCreaNotaCredito,
  handleRigeneraFattura,
  creatingNotaCredito,
  rigeneratingFattura,
  fatturaModificabileEliminabile,
  buildDeleteTarget,
  setDeleteTarget,
}) {
  const inCodaSdi = Boolean(f.sdi_coda_at);
  const ui = statoUiGenerate(f.stato, f);
  const canDelete = fatturaModificabileEliminabile(f) && !inCodaSdi;
  const eliminaSuFic = canDelete && buildDeleteTarget?.(f)?.eliminaFic;
  const showSend = !inCodaSdi && (ui === "generata" || ui === "da_inviare");
  const canCreateNotaCredito =
    f.tipo_documento !== "nota_credito" &&
    ["inviata_sdi", "consegnata", "accettata"].includes(f.stato) &&
    !f.nota_credito_id;
  const canRigenera = f.tipo_documento !== "nota_credito" && f.stato === "stornata";
  const showMore = true;

  const handleDownload = () => {
    if (f.voci?.length > 0) {
      handleOpenFatturaPdf(f);
    } else {
      handleDownloadXml(f.id, f.numero);
    }
  };

  return (
    <div className="flex items-center justify-end gap-1.5">
      <button
        type="button"
        className={ACTION_BTN}
        title="Anteprima / dettaglio"
        onClick={() => openDetail(f.id)}
      >
        <Eye className="w-4 h-4" />
      </button>

      {showSend && (
        <button
          type="button"
          className={cn(ACTION_BTN, "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50/80")}
          title={canInviaSdi ? "Invia a SDI" : "Configura la fatturazione in Impostazioni"}
          disabled={sendingSdi || !canInviaSdi}
          onClick={() => handleInviaSdi(f.id)}
        >
          {sendingSdi ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </button>
      )}

      <button
        type="button"
        className={ACTION_BTN}
        title={f.voci?.length > 0 ? "Scarica PDF" : "Scarica XML"}
        onClick={handleDownload}
      >
        <Download className="w-4 h-4" />
      </button>

      {showMore && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={ACTION_BTN} title="Altre azioni">
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => openDetail(f.id)}>Dettaglio fattura</DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleDownloadXml(f.id, f.numero)}>Scarica XML</DropdownMenuItem>
            {f.voci?.length > 0 && (
              <DropdownMenuItem onClick={() => handleOpenFatturaPdf(f)}>Apri PDF</DropdownMenuItem>
            )}
            {f.fascicolo_id && (
              <DropdownMenuItem asChild>
                <Link to={`/fascicoli-assistenziali/${f.fascicolo_id}?tab=amministrativa`}>
                  Apri fascicolo
                  <ExternalLink className="w-3.5 h-3.5 ml-auto opacity-60" />
                </Link>
              </DropdownMenuItem>
            )}
            {(canCreateNotaCredito || canRigenera) && <DropdownMenuSeparator />}
            {canCreateNotaCredito && (
              <DropdownMenuItem
                onClick={() => handleCreaNotaCredito?.(f)}
                disabled={creatingNotaCredito === f.id}
              >
                {creatingNotaCredito === f.id ? "Creazione NC..." : "Emetti nota di credito"}
              </DropdownMenuItem>
            )}
            {canRigenera && (
              <DropdownMenuItem
                onClick={() => handleRigeneraFattura?.(f)}
                disabled={rigeneratingFattura === f.id}
              >
                {rigeneratingFattura === f.id ? "Rigenerazione..." : "Rigenera fattura"}
              </DropdownMenuItem>
            )}
            {canDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-600"
                  onClick={() => setDeleteTarget(buildDeleteTarget ? buildDeleteTarget(f) : { id: f.id, numero: f.numero })}
                >
                  {eliminaSuFic ? "Elimina su FIC" : "Elimina fattura"}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

const PASSIVE_STATO_ICONS = {
  pagata: CheckCircle2,
  non_pagata: Ban,
  parziale: Clock,
  scaduta: Calendar,
};

function StatoPagamentoBadge({ row, statoMap, formatDataIt, formatEuro, onClick }) {
  const key = row.stato_pagamento_display || row.stato_pagamento || "non_pagata";
  const cfg = statoMap[key] || statoMap.non_pagata;
  const Icon = PASSIVE_STATO_ICONS[key] || Ban;
  const sub =
    key === "pagata" && row.data_pagamento
      ? formatDataIt(row.data_pagamento)
      : key === "parziale" && row.importo_pagato != null
        ? `${formatEuro(row.importo_pagato)}${row.data_pagamento ? ` · ${formatDataIt(row.data_pagamento)}` : ""}`
        : null;
  const inner = (
    <span className={cn("inline-flex flex-col items-start gap-0.5", onClick && "cursor-pointer")}>
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
          cfg.className,
        )}
      >
        <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
        {cfg.label}
      </span>
      {sub && <span className="text-[11px] text-slate-500 pl-0.5">{sub}</span>}
    </span>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="text-left hover:opacity-90">
        {inner}
      </button>
    );
  }
  return inner;
}

function PassiveKpiCard({ icon: Icon, iconClass, title, value, sub, valueClass }) {
  return (
    <Card className="card-base border-slate-200/80 shadow-sm">
      <CardContent className="p-4 flex gap-3">
        <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center shrink-0", iconClass)}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-slate-600">{title}</p>
          <p className={cn("text-xl font-bold mt-0.5 tabular-nums", valueClass || "text-slate-900")}>{value}</p>
          {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function ScadenzaCell({ row, formatDataIt }) {
  const scad = row.data_scadenza ? String(row.data_scadenza).slice(0, 10) : null;
  if (!scad) return <span className="text-slate-400">—</span>;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const scadDate = new Date(`${scad}T12:00:00`);
  const diffDays = Math.round((scadDate - today) / (1000 * 60 * 60 * 24));
  const display = row.stato_pagamento_display || row.stato_pagamento;
  const isPagata = display === "pagata";

  return (
    <div>
      <p className="text-sm text-slate-800">{formatDataIt(scad)}</p>
      {!isPagata && diffDays < 0 && (
        <>
          <p className="text-[11px] text-red-600 font-medium">Scaduta</p>
          <p className="text-[11px] text-red-500">{Math.abs(diffDays)} giorni</p>
        </>
      )}
      {!isPagata && diffDays >= 0 && (
        <p className="text-[11px] text-amber-600 font-medium">
          {diffDays === 0 ? "Oggi" : `${diffDays} giorni`}
        </p>
      )}
    </div>
  );
}

function TableToolbar({
  searchPlaceholder,
  searchQuery,
  onSearchChange,
  filtroStato,
  onFiltroStatoChange,
  filtroMese,
  onFiltroMeseChange,
  mesiOptions,
  formatMese,
  filtroFascicolo,
  onFiltroFascicoloChange,
  fascicoli,
  loadingFascicoli,
  showOspiteFilter,
  extraFilters,
  onRefresh,
  loading,
}) {
  return (
    <div className="flex flex-col gap-3 p-4 border-b border-slate-100 bg-slate-50/50">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            className="pl-9 bg-white"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {filtroStato != null && (
            <Select value={filtroStato} onValueChange={onFiltroStatoChange}>
              <SelectTrigger className="w-[160px] bg-white h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {extraFilters?.statoOptions?.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {filtroMese != null && (
            <Select value={filtroMese} onValueChange={onFiltroMeseChange}>
              <SelectTrigger className="w-[150px] bg-white h-9 text-sm">
                <SelectValue placeholder="Tutti i mesi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__tutti__">Tutti i mesi</SelectItem>
                {mesiOptions.map((m) => (
                  <SelectItem key={m} value={m}>
                    {formatMese(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {showOspiteFilter && (
            <Select value={filtroFascicolo} onValueChange={onFiltroFascicoloChange} disabled={loadingFascicoli}>
              <SelectTrigger className="w-[180px] bg-white h-9 text-sm">
                <SelectValue placeholder="Ospite" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__tutti__">Tutti gli ospiti</SelectItem>
                {fascicoli.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.nome_completo?.trim() || "Ospite"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button type="button" variant="outline" size="sm" className="h-9" onClick={onRefresh} disabled={loading}>
            <Filter className="w-3.5 h-3.5 mr-1.5" />
            Filtri
          </Button>
        </div>
      </div>
    </div>
  );
}

function PaginationBar({ page, lastPage, total, rowsPerPage, onPageChange, onRowsPerPageChange }) {
  const from = total === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const to = Math.min(page * rowsPerPage, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 text-sm text-slate-600">
      <span>
        {total > 0 ? `${from}-${to} di ${total} risultati` : "Nessun risultato"}
      </span>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="px-2 tabular-nums min-w-[4rem] text-center">
            {page} / {lastPage}
          </span>
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={page >= lastPage} onClick={() => onPageChange(page + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Righe per pagina</span>
          <Select value={String(rowsPerPage)} onValueChange={(v) => onRowsPerPageChange(Number(v))}>
            <SelectTrigger className="h-8 w-[72px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[5, 10, 25, 50].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export default function FatturazioneTabsContent(props) {
  const {
    strutturaLabel,
    user,
    currentYear,
    formatEuro,
    formatMese,
    formatDataIt,
    activeTab,
    setActiveTab,
    unifiedKpis,
    kpiConfrontoMensile,
    fattureGenerateStats,
    archivioTotal,
    fattureSdiStats,
    navigate,
    // generate tab
    searchQuery,
    setSearchQuery,
    filtroStato,
    setFiltroStato,
    filtroMese,
    setFiltroMese,
    filtroFascicolo,
    setFiltroFascicolo,
    fascicoli,
    loadingFascicoli,
    mesiOptions,
    FILTRO_STATO_UI,
    loadingFatture,
    loadFatture,
    fattureFiltrate,
    fatturePaginate,
    tablePage,
    setTablePage,
    tableLastPage,
    rowsPerPage,
    setRowsPerPage,
    STATO_BADGE,
    fascicoloLabel,
    openDetail,
    openRecuperoFic,
    handleInviaSdi,
    sendingSdi,
    canInviaSdi,
    handleDownloadXml,
    handleOpenFatturaPdf,
    handleCreaNotaCredito,
    handleRigeneraFattura,
    creatingNotaCredito,
    rigeneratingFattura,
    fatturaModificabileEliminabile,
    buildDeleteTarget,
    setDeleteTarget,
    onBulkDeleteRequest,
    bulkDeleting,
    // SDI tab
    formatDataOraIt,
    sdiStats,
    sdiSearchQuery,
    setSdiSearchQuery,
    sdiFiltroStato,
    setSdiFiltroStato,
    sdiFiltroMese,
    setSdiFiltroMese,
    sdiDataDa,
    setSdiDataDa,
    sdiDataA,
    setSdiDataA,
    sdiMesiOptions,
    clearSdiFilters,
    handleSincronizzaSdi,
    sdiSyncLoading,
    sdiUsageLimitBanner,
    dismissSdiUsageLimitBanner,
    fattureSdiFiltrate,
    fattureSdiPaginate,
    sdiPage,
    setSdiPage,
    sdiLastPage,
    sdiRowsPerPage,
    setSdiRowsPerPage,
    onSollecitaPagamento,
    // passive tab
    hasProviderConfig,
    isFic,
    isAruba,
    fattConfig,
    archivioLoading,
    archivioLoaded,
    archivioItems,
    archivioStats,
    archivioStatsLoading,
    archivioFiltroPagamento,
    setArchivioFiltroPagamento,
    archivioFiltroMese,
    setArchivioFiltroMese,
    archivioSearchQuery,
    setArchivioSearchQuery,
    archivioMesiOptions,
    archivioTotalCount,
    TIPO_PASSIVA_LABELS,
    STATO_PAGAMENTO_PASSIVA_UI,
    handleOpenArchivioPdf,
    clearArchivioFilters,
    archivioDataDa,
    setArchivioDataDa,
    archivioDataA,
    setArchivioDataA,
    archivioFornitore,
    setArchivioFornitore,
    loadArchivioPassive,
    archivioPage,
    archivioLastPage,
    handleDownloadArchivio,
    downloadingArchivio,
    openPassivePayDialog,
    syncLoading,
    syncDataDa,
    setSyncDataDa,
    syncDataA,
    setSyncDataA,
    syncTipoFic,
    setSyncTipoFic,
    handleSincronizzaPassive,
    syncResult,
    passiveDataDa,
    setPassiveDataDa,
    passiveDataA,
    setPassiveDataA,
    passiveTipoFic,
    setPassiveTipoFic,
    loadFatturePassive,
    passiveLoading,
    passiveMessage,
    passiveSearched,
    passiveItems,
    handleDownloadPassiva,
    downloadingPassive,
    passivePage,
    passiveLastPage,
    passiveTotal,
    // fatture attive FIC
    attiveFiltroMese,
    setAttiveFiltroMese,
    attiveMesiOptions,
    attiveItems,
    attiveLoading,
    attiveTotal,
    attiveImportoTotale,
    attivePage,
    attiveLastPage,
    setAttivePage,
    attiveSearchQuery,
    setAttiveSearchQuery,
    loadFattureAttive,
    handleSyncFicNoteCredito,
    syncFicNcLoading,
    handleSyncPagamentiFic,
    syncPagamentiLoading,
    handleOpenAttivaPdf,
    downloadingAttivaPdf,
    setDeleteAttivaTarget,
    // note di credito
    noteCredito,
    fattureEligibiliNc,
    ncSearchQuery,
    setNcSearchQuery,
    ncFiltroMese,
    setNcFiltroMese,
    // controllo mensile
    meseControllo,
    setMeseControllo,
    controlloData,
    controlloLoading,
    loadControllo,
    registerControlloFullRefresh,
    strutturaIdForConfig,
  } = props;

  const [selectedGenerateIds, setSelectedGenerateIds] = useState(() => new Set());

  const isGenerateSelectable = useCallback(
    (f) => {
      if (!f?.id || f.sdi_coda_at) return false;
      return !!fatturaModificabileEliminabile?.(f);
    },
    [fatturaModificabileEliminabile],
  );

  const selectableGenerate = useMemo(
    () => (fattureFiltrate || []).filter(isGenerateSelectable),
    [fattureFiltrate, isGenerateSelectable],
  );

  const allSelectableSelected =
    selectableGenerate.length > 0 &&
    selectableGenerate.every((f) => selectedGenerateIds.has(f.id));

  const someSelectableSelected =
    selectableGenerate.some((f) => selectedGenerateIds.has(f.id)) && !allSelectableSelected;

  useEffect(() => {
    setSelectedGenerateIds((prev) => {
      if (!prev.size) return prev;
      const allowed = new Set(selectableGenerate.map((f) => f.id));
      const next = new Set([...prev].filter((id) => allowed.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [selectableGenerate]);

  useEffect(() => {
    setSelectedGenerateIds(new Set());
  }, [filtroMese, filtroStato, filtroFascicolo, searchQuery]);

  const toggleGenerateSelect = useCallback((id, checked) => {
    setSelectedGenerateIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const toggleSelectAllGenerate = useCallback(
    (checked) => {
      if (!checked) {
        setSelectedGenerateIds(new Set());
        return;
      }
      setSelectedGenerateIds(new Set(selectableGenerate.map((f) => f.id)));
    },
    [selectableGenerate],
  );

  const requestBulkDeleteSelected = useCallback(() => {
    const targets = selectableGenerate
      .filter((f) => selectedGenerateIds.has(f.id))
      .map((f) => buildDeleteTarget?.(f) || { id: f.id, numero: f.numero });
    if (!targets.length) return;
    onBulkDeleteRequest?.(targets);
  }, [selectableGenerate, selectedGenerateIds, buildDeleteTarget, onBulkDeleteRequest]);

  const tabTriggerClass = (color) =>
    `flex-1 sm:flex-none gap-2 data-[state=active]:border-b-2 data-[state=active]:shadow-none rounded-none border-b-2 border-transparent px-4 py-3 ${color}`;

  return (
    <>
      <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
        <Building2 className="w-4 h-4 text-emerald-600 shrink-0" />
        <span>
          Dati riferiti a: <strong>{strutturaLabel}</strong>
          {user?.strutture?.length > 1 && (
            <span className="text-slate-500"> — cambia struttura dal menu in alto se necessario</span>
          )}
        </span>
      </div>

      {!["fatture-passive", "fatture-attive", "fatture-sdi"].includes(activeTab) && (
        <FatturazioneKpiRow kpis={unifiedKpis} formatEuro={formatEuro} />
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex items-end gap-3 border-b border-slate-200">
          <TabsList className="w-full min-w-0 flex-1 h-auto p-0 bg-transparent border-0 rounded-none justify-start gap-0 flex-wrap">
            <TabsTrigger value="controllo-mensile" className={tabTriggerClass("data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-800")}>
              <LayoutDashboard className="w-4 h-4" />
              Controllo mensile
            </TabsTrigger>
            <TabsTrigger value="fatture-generate" className={tabTriggerClass("data-[state=active]:border-emerald-500 data-[state=active]:text-emerald-700")}>
              <Receipt className="w-4 h-4" />
              Fatture generate
            </TabsTrigger>
            <TabsTrigger value="note-credito" className={tabTriggerClass("data-[state=active]:border-purple-500 data-[state=active]:text-purple-700")}>
              <FileMinus2 className="w-4 h-4" />
              Note di credito
            </TabsTrigger>
            <TabsTrigger value="fatture-attive" className={tabTriggerClass("data-[state=active]:border-sky-500 data-[state=active]:text-sky-700")}>
              <FileUp className="w-4 h-4" />
              Fatture attive
            </TabsTrigger>
            <TabsTrigger value="fatture-passive" className={tabTriggerClass("data-[state=active]:border-blue-500 data-[state=active]:text-blue-700")}>
              <Inbox className="w-4 h-4" />
              Fatture passive
            </TabsTrigger>
            <TabsTrigger value="controllo-costi" className={tabTriggerClass("data-[state=active]:border-amber-500 data-[state=active]:text-amber-700")}>
              <TrendingUp className="w-4 h-4" />
              Controllo costi
            </TabsTrigger>
            <TabsTrigger value="fatture-sdi" className={tabTriggerClass("data-[state=active]:border-emerald-500 data-[state=active]:text-emerald-700")}>
              <FileCheck className="w-4 h-4" />
              Fatture inviate (SDI)
            </TabsTrigger>
          </TabsList>
          <Button
            type="button"
            size="sm"
            className="mb-1.5 shrink-0 text-xs bg-[#4CAF50] hover:bg-[#388E3C]"
            onClick={() => navigate("/fatturazione/nuova")}
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Nuova fattura
          </Button>
        </div>

        <TabsContent value="controllo-mensile" className="mt-0">
          <FatturazioneControlloMensileTab
            headers={props.headers}
            meseControllo={meseControllo}
            setMeseControllo={setMeseControllo}
            mesiOptions={mesiOptions}
            formatMese={formatMese}
            formatEuro={formatEuro}
            controlloData={controlloData}
            controlloLoading={controlloLoading}
            loadControllo={loadControllo}
            loadFatture={loadFatture}
            registerControlloFullRefresh={registerControlloFullRefresh}
            fascicoli={fascicoli}
            loadingFascicoli={loadingFascicoli}
            openDetail={openDetail}
            openRecuperoFic={openRecuperoFic}
            canInviaSdi={canInviaSdi}
            isFic={props.isFic}
            strutturaIdForConfig={strutturaIdForConfig}
          />
        </TabsContent>

        <TabsContent value="controllo-costi" className="mt-0">
          <FatturazioneControlloCostiTab
            headers={props.headers}
            formatEuro={formatEuro}
            strutturaIdForConfig={strutturaIdForConfig}
          />
        </TabsContent>

        <TabsContent value="fatture-generate" className="mt-0 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Fatture generate</h2>
              <p className="text-sm text-slate-500 mt-0.5">Elenco delle fatture emesse e del loro stato</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {isFic && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSyncPagamentiFic}
                  disabled={loadingFatture || syncPagamentiLoading}
                >
                  {syncPagamentiLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Euro className="w-4 h-4 mr-2" />
                  )}
                  Sync pagamenti FIC
                </Button>
              )}
              <Button type="button" variant="outline" size="sm" disabled={loadingFatture}>
                <Download className="w-4 h-4 mr-2" />
                Esporta
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={loadFatture} disabled={loadingFatture}>
                {loadingFatture ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Aggiorna
              </Button>
            </div>
          </div>
          <Card className="card-base overflow-hidden">
            <TableToolbar
              searchPlaceholder="Cerca numero, destinatario, ospite…"
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              filtroStato={filtroStato}
              onFiltroStatoChange={setFiltroStato}
              filtroMese={filtroMese}
              onFiltroMeseChange={setFiltroMese}
              mesiOptions={mesiOptions}
              formatMese={formatMese}
              filtroFascicolo={filtroFascicolo}
              onFiltroFascicoloChange={setFiltroFascicolo}
              fascicoli={fascicoli}
              loadingFascicoli={loadingFascicoli}
              showOspiteFilter
              extraFilters={{ statoOptions: FILTRO_STATO_UI }}
              onRefresh={loadFatture}
              loading={loadingFatture}
            />
            {(selectableGenerate.length > 0 || selectedGenerateIds.size > 0) && (
              <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50/80">
                <Checkbox
                  checked={allSelectableSelected ? true : someSelectableSelected ? "indeterminate" : false}
                  onCheckedChange={(v) => toggleSelectAllGenerate(v === true)}
                  disabled={!selectableGenerate.length || bulkDeleting}
                  aria-label="Seleziona tutte le fatture eliminabili"
                />
                <button
                  type="button"
                  className="text-sm font-medium text-slate-700 hover:text-slate-900 disabled:opacity-50"
                  disabled={!selectableGenerate.length || bulkDeleting}
                  onClick={() => toggleSelectAllGenerate(!allSelectableSelected)}
                >
                  {allSelectableSelected ? "Deseleziona tutte" : "Seleziona tutte"}
                  {selectableGenerate.length > 0 && (
                    <span className="text-slate-500 font-normal">
                      {" "}({selectableGenerate.length} eliminabili
                      {filtroMese && filtroMese !== "__tutti__" ? ` · ${formatMese(filtroMese)}` : ""})
                    </span>
                  )}
                </button>
                {selectedGenerateIds.size > 0 && (
                  <>
                    <span className="text-sm text-slate-500">
                      {selectedGenerateIds.size} selezionate
                    </span>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="ml-auto"
                      disabled={bulkDeleting}
                      onClick={requestBulkDeleteSelected}
                    >
                      {bulkDeleting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4 mr-2" />
                      )}
                      Elimina selezionate
                    </Button>
                  </>
                )}
                {filtroMese === "__tutti__" && selectableGenerate.length > 0 && !selectedGenerateIds.size && (
                  <span className="text-xs text-slate-500 sm:ml-auto">
                    Filtra per mese per eliminare tutte le bozze di un periodo
                  </span>
                )}
              </div>
            )}
            {loadingFatture && fattureFiltrate.length === 0 ? (
              <div className="py-16 text-center text-slate-500">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                Caricamento…
              </div>
            ) : fattureFiltrate.length === 0 ? (
              <p className="text-sm text-slate-500 py-12 text-center">Nessuna fattura con i filtri selezionati.</p>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-10" />
                      <TableHead className="text-slate-500 font-medium">Numero</TableHead>
                      <TableHead className="text-slate-500 font-medium">Data</TableHead>
                      <TableHead className="text-slate-500 font-medium">Destinatario</TableHead>
                      <TableHead className="text-slate-500 font-medium">Periodo</TableHead>
                      <TableHead className="text-slate-500 font-medium text-right">Totale</TableHead>
                      <TableHead className="text-slate-500 font-medium">Stato fattura</TableHead>
                      <TableHead className="text-slate-500 font-medium">Stato pagamento</TableHead>
                      <TableHead className="text-slate-500 font-medium text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fatturePaginate.map((f) => {
                      const ospiteNome = fascicoloLabel(fascicoli, f.fascicolo_id) || "";
                      const label = labelDestinatarioOspite({
                        destinatarioNome: f.destinatario_nome,
                        ospiteNome,
                      });
                      const fasc = fascicoli.find((x) => x.id === f.fascicolo_id);
                      const canSelect = isGenerateSelectable(f);
                      const checked = selectedGenerateIds.has(f.id);
                      return (
                        <TableRow key={f.id} className={checked ? "bg-sky-50/40" : undefined}>
                          <TableCell>
                            {canSelect ? (
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(v) => toggleGenerateSelect(f.id, !!v)}
                                disabled={bulkDeleting}
                                aria-label={`Seleziona ${f.numero || f.id}`}
                              />
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <button
                              type="button"
                              onClick={() => openDetail(f.id)}
                              className="inline-flex items-center gap-1.5 font-mono text-sm text-blue-700 hover:underline"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              {f.numero}
                            </button>
                          </TableCell>
                          <TableCell className="text-slate-700">{formatDataIt(f.data_emissione)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 min-w-0">
                              <AvatarInizialiOspite nome={label.ospite !== "—" ? label.ospite : label.primary} />
                              <div className="min-w-0" title={label.full}>
                                <p className="font-medium text-slate-800 truncate">{label.primary}</p>
                                {label.secondary && (
                                  <p className="text-xs text-slate-500 truncate">({label.secondary})</p>
                                )}
                                {fasc?.camera && (
                                  <p className="text-xs text-slate-400 truncate">Camera {fasc.camera}</p>
                                )}
                                {f.ospite_trasferito && (
                                  <Badge className="mt-1 bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-normal">
                                    Ospite trasferito{f.trasferito_a_struttura_nome ? ` a ${f.trasferito_a_struttura_nome}` : ""}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">{formatPeriodoFattura(f)}</TableCell>
                          <TableCell className="text-right font-semibold tabular-nums">{formatEuro(f.totale)}</TableCell>
                          <TableCell>
                            <StatoBadgeGenerate
                              stato={f.stato}
                              map={STATO_FATTURA_GENERATE_UI}
                              resolver={statoFatturaGenerateKey}
                              item={f}
                            />
                          </TableCell>
                          <TableCell>
                            <StatoBadgeGenerate
                              stato={f.stato_pagamento}
                              map={STATO_PAGAMENTO_GENERATE_UI}
                              resolver={statoPagamentoGenerateKey}
                              item={f}
                            />
                          </TableCell>
                          <TableCell>
                            <FatturaGenerateAzioni
                              f={f}
                              openDetail={openDetail}
                              handleInviaSdi={handleInviaSdi}
                              sendingSdi={sendingSdi}
                              canInviaSdi={canInviaSdi}
                              handleDownloadXml={handleDownloadXml}
                              handleOpenFatturaPdf={handleOpenFatturaPdf}
                              handleCreaNotaCredito={handleCreaNotaCredito}
                              handleRigeneraFattura={handleRigeneraFattura}
                              creatingNotaCredito={creatingNotaCredito}
                              rigeneratingFattura={rigeneratingFattura}
                              fatturaModificabileEliminabile={fatturaModificabileEliminabile}
                              buildDeleteTarget={buildDeleteTarget}
                              setDeleteTarget={setDeleteTarget}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <PaginationBar
                  page={tablePage}
                  lastPage={tableLastPage}
                  total={fattureFiltrate.length}
                  rowsPerPage={rowsPerPage}
                  onPageChange={setTablePage}
                  onRowsPerPageChange={setRowsPerPage}
                />
              </>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="note-credito" className="mt-0">
          <FatturazioneNoteCreditoTab
            noteCredito={noteCredito}
            fattureEligibiliNc={fattureEligibiliNc}
            formatEuro={formatEuro}
            formatMese={formatMese}
            formatDataIt={formatDataIt}
            fascicoloLabel={fascicoloLabel}
            loadingFatture={loadingFatture}
            loadFatture={loadFatture}
            openDetail={openDetail}
            handleInviaSdi={handleInviaSdi}
            sendingSdi={sendingSdi}
            canInviaSdi={canInviaSdi}
            handleDownloadXml={handleDownloadXml}
            handleOpenFatturaPdf={handleOpenFatturaPdf}
            handleCreaNotaCredito={handleCreaNotaCredito}
            creatingNotaCredito={creatingNotaCredito}
            handleSyncFicNoteCredito={handleSyncFicNoteCredito}
            syncFicNcLoading={syncFicNcLoading}
            isFic={isFic}
            filtroMese={ncFiltroMese}
            setFiltroMese={setNcFiltroMese}
            mesiOptions={mesiOptions}
            searchQuery={ncSearchQuery}
            setSearchQuery={setNcSearchQuery}
          />
        </TabsContent>

        <TabsContent value="fatture-attive" className="mt-0">
          <FatturazioneFattureAttiveTab
            hasProviderConfig={hasProviderConfig}
            isFic={isFic}
            attiveFiltroMese={attiveFiltroMese}
            setAttiveFiltroMese={setAttiveFiltroMese}
            attiveMesiOptions={attiveMesiOptions}
            formatMese={formatMese}
            formatEuro={formatEuro}
            attiveItems={attiveItems}
            attiveLoading={attiveLoading}
            attiveTotal={attiveTotal}
            attiveImportoTotale={attiveImportoTotale}
            kpiConfrontoMensile={kpiConfrontoMensile}
            attivePage={attivePage}
            attiveLastPage={attiveLastPage}
            setAttivePage={setAttivePage}
            attiveSearchQuery={attiveSearchQuery}
            setAttiveSearchQuery={setAttiveSearchQuery}
            loadFattureAttive={loadFattureAttive}
            handleSyncFicNoteCredito={handleSyncFicNoteCredito}
            syncFicNcLoading={syncFicNcLoading}
            handleSyncPagamentiFic={handleSyncPagamentiFic}
            syncPagamentiLoading={syncPagamentiLoading}
            handleOpenAttivaPdf={handleOpenAttivaPdf}
            downloadingAttivaPdf={downloadingAttivaPdf}
            setDeleteAttivaTarget={setDeleteAttivaTarget}
          />
        </TabsContent>

        <TabsContent value="fatture-sdi" className="mt-0">
          <FatturazioneSdiTab
            currentYear={currentYear}
            formatEuro={formatEuro}
            formatDataIt={formatDataIt}
            formatDataOraIt={formatDataOraIt}
            hasProviderConfig={hasProviderConfig}
            isFic={isFic}
            isAruba={isAruba}
            sdiStats={sdiStats}
            sdiSearchQuery={sdiSearchQuery}
            setSdiSearchQuery={setSdiSearchQuery}
            sdiFiltroStato={sdiFiltroStato}
            setSdiFiltroStato={setSdiFiltroStato}
            sdiFiltroMese={sdiFiltroMese}
            setSdiFiltroMese={setSdiFiltroMese}
            sdiDataDa={sdiDataDa}
            setSdiDataDa={setSdiDataDa}
            sdiDataA={sdiDataA}
            setSdiDataA={setSdiDataA}
            sdiMesiOptions={sdiMesiOptions}
            formatMese={formatMese}
            clearSdiFilters={clearSdiFilters}
            handleSincronizzaSdi={handleSincronizzaSdi}
            sdiSyncLoading={sdiSyncLoading}
            sdiUsageLimitBanner={sdiUsageLimitBanner}
            dismissSdiUsageLimitBanner={dismissSdiUsageLimitBanner}
            loadingFatture={loadingFatture}
            fattureSdiFiltrate={fattureSdiFiltrate}
            fattureSdiPaginate={fattureSdiPaginate}
            sdiPage={sdiPage}
            setSdiPage={setSdiPage}
            sdiLastPage={sdiLastPage}
            sdiRowsPerPage={sdiRowsPerPage}
            setSdiRowsPerPage={setSdiRowsPerPage}
            openDetail={openDetail}
            handleOpenFatturaPdf={handleOpenFatturaPdf}
            handleDownloadXml={handleDownloadXml}
            handleInviaSdi={handleInviaSdi}
            sendingSdi={sendingSdi}
            canInviaSdi={canInviaSdi}
            onSollecitaPagamento={onSollecitaPagamento}
            fascicoloLabel={(fid) => fascicoloLabel(fascicoli, fid)}
          />
        </TabsContent>

        <TabsContent value="fatture-passive" className="mt-0 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Fatture passive</h2>
              <p className="text-sm text-slate-500 mt-0.5">Gestisci le fatture ricevute dai fornitori</p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              {hasProviderConfig && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleSincronizzaPassive()}
                  disabled={syncLoading}
                >
                  {syncLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  {isFic ? "Sincronizza da Fatture in Cloud" : "Sincronizza da provider"}
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                className="bg-[#00B074] hover:bg-[#009966] text-white"
                disabled={!hasProviderConfig}
                title={hasProviderConfig ? "Registra una fattura passiva manuale" : "Configura il provider in Impostazioni"}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuova fattura passiva
              </Button>
            </div>
          </div>

          {!hasProviderConfig ? (
            <Card className="card-base">
              <CardContent className="p-6 text-sm text-slate-500">
                Configura Fatture in Cloud o Aruba in Impostazioni per importare le fatture passive, visualizzare PDF e scaricare XML.
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <PassiveKpiCard
                  icon={TrendingUp}
                  iconClass="bg-emerald-100 text-emerald-700"
                  title={kpiConfrontoMensile?.periodo === "anno" ? "Attive anno" : "Attive del mese"}
                  value={
                    kpiConfrontoMensile?.loading
                      ? "…"
                      : formatEuro(kpiConfrontoMensile?.attive?.importo ?? 0)
                  }
                  sub={
                    kpiConfrontoMensile?.loading
                      ? undefined
                      : `${kpiConfrontoMensile?.attive?.count ?? 0} fatture · ${
                          kpiConfrontoMensile?.mese
                            ? formatMese(kpiConfrontoMensile.mese)
                            : `Anno ${kpiConfrontoMensile?.anno ?? currentYear}`
                        }`
                  }
                />
                <PassiveKpiCard
                  icon={Inbox}
                  iconClass="bg-blue-100 text-blue-700"
                  title={kpiConfrontoMensile?.periodo === "anno" ? "Passive anno" : "Passive del mese"}
                  value={
                    kpiConfrontoMensile?.loading
                      ? "…"
                      : formatEuro(kpiConfrontoMensile?.passive?.importo ?? 0)
                  }
                  sub={
                    kpiConfrontoMensile?.loading
                      ? undefined
                      : `${kpiConfrontoMensile?.passive?.count ?? 0} fatture · costi fornitori`
                  }
                />
                <PassiveKpiCard
                  icon={ArrowDownUp}
                  iconClass={
                    (kpiConfrontoMensile?.differenza ?? 0) >= 0
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-red-100 text-red-700"
                  }
                  title="Differenza (attive − passive)"
                  value={
                    kpiConfrontoMensile?.loading
                      ? "…"
                      : formatEuro(kpiConfrontoMensile?.differenza ?? 0)
                  }
                  valueClass={
                    (kpiConfrontoMensile?.differenza ?? 0) >= 0 ? "text-emerald-700" : "text-red-700"
                  }
                  sub="Indicatore di controllo (non è utile fiscale)"
                />
                <PassiveKpiCard
                  icon={Hourglass}
                  iconClass="bg-amber-100 text-amber-700"
                  title="Da pagare"
                  value={
                    kpiConfrontoMensile?.loading
                      ? "…"
                      : (kpiConfrontoMensile?.da_pagare?.count ?? "—")
                  }
                  sub={
                    kpiConfrontoMensile?.da_pagare?.importo != null
                      ? formatEuro(kpiConfrontoMensile.da_pagare.importo)
                      : undefined
                  }
                />
                <PassiveKpiCard
                  icon={Calendar}
                  iconClass="bg-red-100 text-red-700"
                  title="Scadute"
                  value={
                    kpiConfrontoMensile?.loading
                      ? "…"
                      : (kpiConfrontoMensile?.scadute?.count ?? "—")
                  }
                  sub={
                    kpiConfrontoMensile?.scadute?.importo != null
                      ? formatEuro(kpiConfrontoMensile.scadute.importo)
                      : undefined
                  }
                />
              </div>

              <Card className="card-base overflow-hidden">
                <div className="flex flex-col gap-3 p-4 border-b border-slate-100 bg-slate-50/50">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        className="pl-9 bg-white"
                        placeholder="Cerca fornitore, numero, importo…"
                        value={archivioSearchQuery}
                        onChange={(e) => setArchivioSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && loadArchivioPassive(1)}
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Select value={archivioFiltroPagamento} onValueChange={setArchivioFiltroPagamento}>
                        <SelectTrigger className="w-[160px] bg-white h-9 text-sm">
                          <SelectValue placeholder="Tutti gli stati" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__tutti__">Tutti gli stati</SelectItem>
                          <SelectItem value="non_pagata">Da pagare</SelectItem>
                          <SelectItem value="pagata">Pagata</SelectItem>
                          <SelectItem value="parziale">Parzialmente pagata</SelectItem>
                          <SelectItem value="scaduta">Scaduta</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={archivioFiltroMese} onValueChange={setArchivioFiltroMese}>
                        <SelectTrigger className="w-[150px] bg-white h-9 text-sm">
                          <SelectValue placeholder="Tutti i mesi" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__tutti__">Tutti i mesi</SelectItem>
                          {archivioMesiOptions.map((m) => (
                            <SelectItem key={m} value={m}>
                              {formatMese(m)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-9"
                        onClick={() => loadArchivioPassive(1)}
                        disabled={archivioLoading}
                      >
                        <Filter className="w-3.5 h-3.5 mr-1.5" />
                        Filtri
                      </Button>
                      <button
                        type="button"
                        className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                        onClick={() => {
                          clearArchivioFilters();
                          loadArchivioPassive(1);
                        }}
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Pulisci
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <RotateCcw className="w-3 h-3" />
                    Sincronizzazione automatica giornaliera da {isFic ? "Fatture in Cloud" : isAruba ? "Aruba" : "provider"}
                  </p>
                </div>

                {archivioLoading && archivioItems.length === 0 ? (
                  <div className="py-16 text-center text-slate-500">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                    Caricamento…
                  </div>
                ) : archivioLoaded && archivioItems.length === 0 ? (
                  <p className="text-sm text-slate-500 py-12 text-center">
                    Nessuna fattura passiva per i filtri selezionati. Usa Sincronizza per importare da Fatture in Cloud.
                  </p>
                ) : (
                  <>
                    <div className="[&>div]:overflow-x-visible">
                      <Table className="table-fixed w-full text-xs">
                      <TableHeader>
                        <TableRow className="hover:bg-transparent">
                          <TableHead className="text-slate-500 font-medium w-[9%] px-1.5">Ricezione</TableHead>
                          <TableHead className="text-slate-500 font-medium w-[22%] px-1.5">Fornitore</TableHead>
                          <TableHead className="text-slate-500 font-medium w-[10%] px-1.5">Numero</TableHead>
                          <TableHead className="text-slate-500 font-medium w-[8%] px-1.5">Tipo</TableHead>
                          <TableHead className="text-slate-500 font-medium text-right w-[10%] px-1.5">Importo</TableHead>
                          <TableHead className="text-slate-500 font-medium w-[14%] px-1.5">Stato pag.</TableHead>
                          <TableHead className="text-slate-500 font-medium w-[10%] px-1.5">Data pag.</TableHead>
                          <TableHead className="text-slate-500 font-medium w-[12%] px-1.5">Scadenza</TableHead>
                          <TableHead className="text-slate-500 font-medium text-right w-[5%] px-1.5">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {archivioItems.map((row) => {
                          const tipoLabel =
                            row.tipo_label ||
                            TIPO_PASSIVA_LABELS[row.tipo_documento] ||
                            row.tipo_documento ||
                            "—";
                          const ricezione = row.data_ricezione || row.data;
                          return (
                            <TableRow key={row.id}>
                              <TableCell className="px-1.5 whitespace-nowrap text-slate-700">
                                {ricezione ? formatDataIt(String(ricezione).slice(0, 10)) : "—"}
                              </TableCell>
                              <TableCell className="px-1.5 min-w-0">
                                <p className="font-medium text-slate-800 truncate" title={row.fornitore}>
                                  {row.fornitore || "—"}
                                </p>
                                {row.fornitore_piva && (
                                  <p className="text-[10px] text-slate-500 truncate">P.IVA {row.fornitore_piva}</p>
                                )}
                              </TableCell>
                              <TableCell className="font-mono px-1.5 truncate">{row.numero || "—"}</TableCell>
                              <TableCell className="px-1.5">
                                <Badge variant="secondary" className="font-normal text-[10px] px-1.5 py-0">
                                  {tipoLabel}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right font-semibold px-1.5 whitespace-nowrap">
                                {row.totale != null ? formatEuro(row.totale) : "—"}
                              </TableCell>
                              <TableCell className="px-1.5">
                                <StatoPagamentoBadge
                                  row={row}
                                  statoMap={STATO_PAGAMENTO_PASSIVA_UI}
                                  formatDataIt={formatDataIt}
                                  formatEuro={formatEuro}
                                  onClick={() => openPassivePayDialog(row)}
                                />
                              </TableCell>
                              <TableCell className="px-1.5 whitespace-nowrap text-slate-700">
                                {row.data_pagamento ? formatDataIt(row.data_pagamento) : "—"}
                              </TableCell>
                              <TableCell className="px-1.5">
                                <ScadenzaCell row={row} formatDataIt={formatDataIt} />
                              </TableCell>
                              <TableCell className="px-1">
                                <div className="flex items-center justify-end gap-0.5">
                                  {(row.source === "fatture_in_cloud" || isFic) && (
                                    <button
                                      type="button"
                                      className={cn(ACTION_BTN, "h-7 w-7")}
                                      title="Visualizza PDF"
                                      disabled={!!downloadingArchivio}
                                      onClick={() => handleOpenArchivioPdf(row)}
                                    >
                                      <Eye className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className={cn(ACTION_BTN, "h-7 w-7")}
                                    title="Scarica XML"
                                    disabled={!!downloadingArchivio}
                                    onClick={() => handleDownloadArchivio(row, "xml")}
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                  </button>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <button type="button" className={cn(ACTION_BTN, "h-7 w-7")} title="Altre azioni">
                                        <MoreVertical className="w-3.5 h-3.5" />
                                      </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-48">
                                      <DropdownMenuItem onClick={() => openPassivePayDialog(row)}>
                                        Stato pagamento
                                      </DropdownMenuItem>
                                      {(row.source === "fatture_in_cloud" || isFic) && (
                                        <DropdownMenuItem onClick={() => handleOpenArchivioPdf(row)}>
                                          Apri PDF
                                        </DropdownMenuItem>
                                      )}
                                      <DropdownMenuItem onClick={() => handleDownloadArchivio(row, "xml")}>
                                        Scarica XML
                                      </DropdownMenuItem>
                                      {(row.source === "fatture_in_cloud" || isFic) && (
                                        <DropdownMenuItem onClick={() => handleDownloadArchivio(row, "pdf")}>
                                          Scarica PDF
                                        </DropdownMenuItem>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    </div>
                    <PaginationBar
                      page={archivioPage}
                      lastPage={archivioLastPage}
                      total={archivioTotalCount}
                      rowsPerPage={50}
                      onPageChange={(p) => loadArchivioPassive(p)}
                      onRowsPerPageChange={() => {}}
                    />
                  </>
                )}
              </Card>

              <details className="text-xs text-slate-500 rounded-lg border border-slate-200 bg-white px-4 py-3">
                <summary className="cursor-pointer hover:text-slate-700 font-medium text-sm text-slate-600">
                  Sincronizzazione manuale e ricerca live sul provider
                </summary>
                <div className="mt-3 space-y-3 pt-3 border-t">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-2">
                      <Label className="text-xs">Da</Label>
                      <Input type="date" className="mt-1" value={syncDataDa} onChange={(e) => setSyncDataDa(e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs">A</Label>
                      <Input type="date" className="mt-1" value={syncDataA} onChange={(e) => setSyncDataA(e.target.value)} />
                    </div>
                    {isFic && (
                      <div className="md:col-span-4">
                        <Label className="text-xs">Tipo documento</Label>
                        <Select value={syncTipoFic} onValueChange={setSyncTipoFic}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="expense">Spesa / acquisto</SelectItem>
                            <SelectItem value="passive_credit_note">Nota di credito passiva</SelectItem>
                            <SelectItem value="passive_delivery_note">DDT passivo</SelectItem>
                            <SelectItem value="self_invoice">Autofattura</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className={isFic ? "md:col-span-4" : "md:col-span-8"}>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={handleSincronizzaPassive}
                        disabled={syncLoading}
                      >
                        {syncLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                        Sincronizza intervallo
                      </Button>
                    </div>
                  </div>
                  {syncResult && (
                    <div className="flex items-start gap-2 text-sm rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                      <span className="text-emerald-900">{syncResult.message}</span>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-2">
                      <Label className="text-xs">Da (live)</Label>
                      <Input type="date" className="mt-1" value={passiveDataDa} onChange={(e) => setPassiveDataDa(e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs">A (live)</Label>
                      <Input type="date" className="mt-1" value={passiveDataA} onChange={(e) => setPassiveDataA(e.target.value)} />
                    </div>
                    <div className="md:col-span-4">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full mt-5"
                        onClick={() => loadFatturePassive(1)}
                        disabled={passiveLoading}
                      >
                        Carica elenco live
                      </Button>
                    </div>
                  </div>
                  {passiveMessage && (
                    <p className="text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1">{passiveMessage}</p>
                  )}
                  {passiveItems.length > 0 && (
                    <p className="text-slate-600">
                      {passiveTotal} documenti (pag. {passivePage}/{passiveLastPage}) sul provider — usa Sincronizza per archiviarli.
                    </p>
                  )}
                </div>
              </details>
            </>
          )}
        </TabsContent>
      </Tabs>

    </>
  );
}
